import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { SettingsInput, SettingsRow, SegmentedPicker } from './SettingsPrimitives';
import { NODE_UI } from '../../../NodeUI/nodeUiStyles';

/* ─── Typed constraints per field type ─── */

// Per-type min/max constraint inputs. `numeric` types render number inputs with a
// "—" placeholder and clear to undefined on empty; date/time render text inputs with
// a format hint. Each entry lists its two [key, label] constraint fields.
const TYPED_CONSTRAINTS = {
  text: { numeric: true, fields: [['minLength', 'Min length'], ['maxLength', 'Max length']] },
  number: { numeric: true, fields: [['min', 'Min value'], ['max', 'Max value']] },
  checkbox: {
    numeric: true,
    fields: [['minSelected', 'Min selected'], ['maxSelected', 'Max selected']],
  },
  list: { numeric: true, fields: [['minItems', 'Min items'], ['maxItems', 'Max items']] },
  date: { placeholder: 'YYYY-MM-DD', fields: [['minDate', 'Min date'], ['maxDate', 'Max date']] },
  time: { placeholder: 'HH:mm', fields: [['minTime', 'Min time'], ['maxTime', 'Max time']] },
};

function hasTypedConstraints(fieldType) {
  return Boolean(TYPED_CONSTRAINTS[fieldType]);
}

function TypedConstraintsEditor({ field, validations, onValidationsChange, palette, theme }) {
  const config = TYPED_CONSTRAINTS[field.type];
  if (!config) return null;
  const typed = validations.typed || {};
  const updateTyped = (patch) => onValidationsChange({ ...validations, typed: { ...typed, ...patch } });
  const numeric = Boolean(config.numeric);

  return (
    <SettingsRow>
      {config.fields.map(([key, label]) => (
        <SettingsInput
          key={key}
          label={label}
          value={typed[key] ?? ''}
          onChange={(v) =>
            updateTyped({ [key]: numeric ? (v === '' ? undefined : v) : v || undefined })
          }
          placeholder={config.placeholder || '—'}
          type={numeric ? 'number' : undefined}
          palette={palette}
          theme={theme}
        />
      ))}
    </SettingsRow>
  );
}

/* ─── Regex validation editor ─── */

function RegexEditor({ validations, onValidationsChange, palette, theme }) {
  const regex = validations.regex || {};
  const updateRegex = (patch) => onValidationsChange({ ...validations, regex: { ...regex, ...patch } });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      <SettingsRow>
        <SettingsInput
          label="Pattern"
          value={regex.pattern ?? ''}
          onChange={(v) => updateRegex({ pattern: v })}
          placeholder="^[A-Z].*"
          mono
          palette={palette}
          theme={theme}
        />
        <Box sx={{ maxWidth: 80 }}>
          <SettingsInput
            label="Flags"
            value={regex.flags ?? ''}
            onChange={(v) => updateRegex({ flags: v })}
            placeholder="i"
            mono
            palette={palette}
            theme={theme}
          />
        </Box>
      </SettingsRow>
      <SettingsInput
        label="Error message"
        value={regex.errorMessage ?? ''}
        onChange={(v) => updateRegex({ errorMessage: v })}
        placeholder="Input does not match required format"
        palette={palette}
        theme={theme}
      />
    </Box>
  );
}

/* ─── NL requirement editor ─── */

function RequirementEditor({ validations, onValidationsChange, palette, theme }) {
  const req = validations.requirement || {};
  const updateReq = (patch) => onValidationsChange({ ...validations, requirement: { ...req, ...patch } });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      <SettingsInput
        label="Requirement (natural language)"
        value={req.requirementsText ?? ''}
        onChange={(v) => updateReq({ requirementsText: v })}
        placeholder="e.g. Answer must include a company name"
        palette={palette}
        theme={theme}
      />
      <SettingsRow>
        <SegmentedPicker
          label="Mode"
          value={req.evaluationMode || 'ADVISORY'}
          options={[
            { value: 'ADVISORY', label: 'Advisory' },
            { value: 'BLOCKING', label: 'Blocking' },
          ]}
          onChange={(v) => updateReq({ evaluationMode: v })}
          palette={palette}
        />
      </SettingsRow>
      <SettingsInput
        label="Failure message"
        value={req.failureMessage ?? ''}
        onChange={(v) => updateReq({ failureMessage: v })}
        placeholder="Requirement not met."
        palette={palette}
        theme={theme}
      />
    </Box>
  );
}

/* ─── Validation section wrapper ─── */

export default function ValidationSection({ field, validations, onValidationsChange, palette, theme }) {
  const [expandedSections, setExpandedSections] = useState(() => {
    const init = {};
    if (validations.typed && Object.values(validations.typed).some((v) => v !== undefined && v !== '')) init.typed = true;
    if (validations.regex?.pattern) init.regex = true;
    if (validations.requirement?.requirementsText) init.requirement = true;
    return init;
  });

  const toggle = (key) => setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));

  const showTypedConstraints = hasTypedConstraints(field.type);
  const isDisplay = field.type === 'display';
  if (isDisplay) return null;

  const sectionBtnSx = (active) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    px: '8px',
    py: '3px',
    borderRadius: NODE_UI.borderRadiusSm,
    border: `1px solid ${active ? palette.textSecondary : palette.border}`,
    backgroundColor: active ? palette.surface : 'transparent',
    cursor: 'pointer',
    userSelect: 'none',
    transition: 'all 0.12s ease',
    '&:hover': { borderColor: palette.textSecondary },
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {/* Toggle buttons for validation modes */}
      <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
        {showTypedConstraints && (
          <Box onClick={(e) => { e.stopPropagation(); toggle('typed'); }} sx={sectionBtnSx(expandedSections.typed)}>
            <Typography sx={{ fontSize: '11px', fontWeight: expandedSections.typed ? 600 : 400, color: expandedSections.typed ? palette.textPrimary : palette.textSecondary }}>
              Constraints
            </Typography>
          </Box>
        )}
        <Box onClick={(e) => { e.stopPropagation(); toggle('regex'); }} sx={sectionBtnSx(expandedSections.regex)}>
          <Typography sx={{ fontSize: '11px', fontWeight: expandedSections.regex ? 600 : 400, color: expandedSections.regex ? palette.textPrimary : palette.textSecondary }}>
            Regex
          </Typography>
        </Box>
        <Box onClick={(e) => { e.stopPropagation(); toggle('requirement'); }} sx={sectionBtnSx(expandedSections.requirement)}>
          <Typography sx={{ fontSize: '11px', fontWeight: expandedSections.requirement ? 600 : 400, color: expandedSections.requirement ? palette.textPrimary : palette.textSecondary }}>
            AI Requirement
          </Typography>
        </Box>
      </Box>

      {/* Expanded sections */}
      {expandedSections.typed && showTypedConstraints && (
        <TypedConstraintsEditor
          field={field}
          validations={validations}
          onValidationsChange={onValidationsChange}
          palette={palette}
          theme={theme}
        />
      )}
      {expandedSections.regex && (
        <RegexEditor
          validations={validations}
          onValidationsChange={onValidationsChange}
          palette={palette}
          theme={theme}
        />
      )}
      {expandedSections.requirement && (
        <RequirementEditor
          validations={validations}
          onValidationsChange={onValidationsChange}
          palette={palette}
          theme={theme}
        />
      )}
    </Box>
  );
}
